import React, { useState } from "react";
import {
  Button, Badge, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, Row, Col,
} from "reactstrap";

/**
 * Date filtering for the research list.
 *
 * Two different questions get asked of the same rows and they are not the same
 * filter: "what did I record today" looks at when the forecast was made, and
 * "what lands today" looks at when it resolves. Both are offered, plus a custom
 * range over either field.
 *
 * Bounds are computed here, in the browser, and sent as absolute instants. The
 * server stores UTC and has no idea where the member is; a day that starts at
 * their midnight can only be worked out on their side.
 */

const startOfDay = (d = new Date()) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const endOfDay = (d = new Date()) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const iso = (d) => (d ? d.toISOString() : null);

/**
 * Each preset produces the same shape the custom range does, so nothing
 * downstream has to know which one was used.
 */
export const DATE_PRESETS = [
  {
    id: "created_today",
    label: "Created today",
    hint: "Forecasts you recorded since midnight",
    build: () => ({ createdFrom: iso(startOfDay()), createdTo: iso(endOfDay()) }),
  },
  {
    id: "target_today",
    label: "Resolving today",
    hint: "Windows that close before midnight tonight",
    build: () => ({ targetFrom: iso(startOfDay()), targetTo: iso(endOfDay()) }),
  },
  {
    id: "target_overdue",
    label: "Already resolved",
    hint: "Horizons that have passed",
    build: () => ({ targetTo: iso(new Date()) }),
  },
  {
    id: "target_open",
    label: "Still open",
    hint: "Not due yet",
    build: () => ({ targetFrom: iso(new Date()) }),
  },
  {
    id: "created_7d",
    label: "Created this week",
    hint: "The last seven days",
    build: () => ({ createdFrom: iso(startOfDay(addDays(new Date(), -6))), createdTo: iso(endOfDay()) }),
  },
  {
    id: "created_30d",
    label: "Created this month",
    hint: "The last thirty days",
    build: () => ({ createdFrom: iso(startOfDay(addDays(new Date(), -29))), createdTo: iso(endOfDay()) }),
  },
];

const FIELDS = [
  { id: "created", label: "Created", from: "createdFrom", to: "createdTo" },
  { id: "target", label: "Resolves", from: "targetFrom", to: "targetTo" },
];

/** A human summary of whatever range is active, for the chip. */
export const describeDateFilter = (dates) => {
  if (!dates || !dates.preset) {
    if (!dates) return null;
    const parts = [];
    for (const f of FIELDS) {
      const from = dates[f.from];
      const to = dates[f.to];
      if (!from && !to) continue;
      const fmt = (v) => new Date(v).toLocaleDateString();
      if (from && to) parts.push(`${f.label} ${fmt(from)}–${fmt(to)}`);
      else if (from) parts.push(`${f.label} from ${fmt(from)}`);
      else parts.push(`${f.label} until ${fmt(to)}`);
    }
    return parts.length ? parts.join(", ") : null;
  }
  const preset = DATE_PRESETS.find((p) => p.id === dates.preset);
  return preset ? preset.label : null;
};

/** The value of a date input, from an ISO instant, in local time. */
const toDateInput = (isoString) => {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const DateFilter = ({ value, onChange, loading }) => {
  const [open, setOpen] = useState(false);
  const [field, setField] = useState("created");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const active = describeDateFilter(value);

  const openDialog = () => {
    // Seed the dialog from whatever is active, so reopening it shows the
    // current range rather than a blank form.
    const seeded = FIELDS.find((f) => value && (value[f.from] || value[f.to]));
    if (seeded) {
      setField(seeded.id);
      setFrom(toDateInput(value[seeded.from]));
      setTo(toDateInput(value[seeded.to]));
    } else {
      setField("created");
      setFrom("");
      setTo("");
    }
    setOpen(true);
  };

  const applyPreset = (preset) => {
    onChange({ preset: preset.id, ...preset.build() });
    setOpen(false);
  };

  const applyCustom = () => {
    const f = FIELDS.find((x) => x.id === field);
    const next = {};
    // Whole days: from midnight of the first to the last moment of the second,
    // so picking the same date twice means that one day rather than an instant.
    if (from) next[f.from] = iso(startOfDay(new Date(`${from}T00:00:00`)));
    if (to) next[f.to] = iso(endOfDay(new Date(`${to}T00:00:00`)));
    onChange(Object.keys(next).length ? next : null);
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
    setOpen(false);
  };

  return (
    <>
      <Button size="sm" outline={!active} color={active ? "primary" : "secondary"}
              disabled={loading} onClick={openDialog}>
        <i className="ri-calendar-line align-bottom me-1" />
        Date
        {active && <Badge color="light" className="text-primary ms-1">1</Badge>}
      </Button>

      <Modal isOpen={open} toggle={() => setOpen(false)} centered>
        <ModalHeader toggle={() => setOpen(false)}>Filter by date</ModalHeader>
        <ModalBody>
          <div className="text-muted fs-12 mb-2">Quick ranges</div>
          <div className="d-flex flex-wrap gap-2 mb-4">
            {DATE_PRESETS.map((preset) => (
              <Button
                key={preset.id}
                size="sm"
                color={value?.preset === preset.id ? "primary" : "light"}
                title={preset.hint}
                onClick={() => applyPreset(preset)}
              >
                {preset.label}
              </Button>
            ))}
          </div>

          <div className="text-muted fs-12 mb-2">Custom range</div>
          <Row className="g-2">
            <Col sm={4}>
              <Label for="date-field" className="fs-13">Field</Label>
              <Input type="select" id="date-field" bsSize="sm" value={field}
                     onChange={(e) => setField(e.target.value)}>
                {FIELDS.map((f) => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </Input>
            </Col>
            <Col sm={4}>
              <Label for="date-from" className="fs-13">From</Label>
              <Input type="date" id="date-from" bsSize="sm" value={from}
                     max={to || undefined}
                     onChange={(e) => setFrom(e.target.value)} />
            </Col>
            <Col sm={4}>
              <Label for="date-to" className="fs-13">To</Label>
              <Input type="date" id="date-to" bsSize="sm" value={to}
                     min={from || undefined}
                     onChange={(e) => setTo(e.target.value)} />
            </Col>
          </Row>
          <p className="text-muted fs-12 mt-2 mb-0">
            Leave either side empty for an open-ended range. Dates are your local
            days, not UTC.
          </p>
        </ModalBody>
        <ModalFooter className="justify-content-between">
          <Button size="sm" color="link" className="text-decoration-none"
                  disabled={!active} onClick={clear}>
            Clear
          </Button>
          <div className="d-flex gap-2">
            <Button color="light" onClick={() => setOpen(false)}>Cancel</Button>
            <Button color="primary" onClick={applyCustom} disabled={!from && !to}>
              Apply range
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default DateFilter;
